import {type ForwardRefExoticComponent, useMemo} from 'react';

import * as motion from 'motion/react-m';

/**
 * The `useInViewAnimContainer` hook wraps a given base component with motion capabilities for in-view animations.
 * It uses `motion.create` to produce an animated version of the provided component, memoizing the result to avoid unnecessary re-creations on re-renders.
 * This hook is essential for enabling declarative scroll-triggered animations on arbitrary forwardRef components.
 *
 * @param baseComponent The forwardRef component to be enhanced with motion animation capabilities.
 * @returns An object containing `Base` as the motion-enabled version of the provided component ready for animation props.
 *
 * @example
 * ```tsx
 * const {Base} = useInViewAnimContainer(StyledDiv);
 *
 * return <Base initial={{opacity: 0}} animate={{opacity: 1}} />;
 * ```
 */
export const useInViewAnimContainer = (baseComponent: ForwardRefExoticComponent<unknown>) => {
    const Base = useMemo(() => motion.create(baseComponent), [baseComponent]);

    return {Base};
};